import { NavSection } from '@trueform/ui/layout';
import { AuthAction, AuthResource } from '@trueform/ui/core';
import {
  COUNTRIES_VIEW_PERMISSION,
  CURRENCIES_VIEW_PERMISSION,
  DDU_TARIFFS_VIEW_PERMISSION,
  DESTINATION_CITIES_VIEW_PERMISSION,
  DXV_CONFIGURATIONS_VIEW_PERMISSION,
  EXTERNAL_CONTEXTS_VIEW_PERMISSION,
  FREIGHT_TARIFFS_VIEW_PERMISSION,
  FX_RATES_VIEW_PERMISSION,
  HUBS_VIEW_PERMISSION,
  IMPORT_TAXES_VIEW_PERMISSION,
  IVA_RATES_VIEW_PERMISSION,
  PACKAGINGS_VIEW_PERMISSION,
  PACKAGING_TARIFFS_VIEW_PERMISSION,
  PAYMENT_TERM_CHARGES_VIEW_PERMISSION,
  PAYMENT_TERMS_VIEW_PERMISSION,
  PCS_CALCULATOR_VIEW_PERMISSION,
  PRICE_SOURCES_VIEW_PERMISSION,
  PRICES_VIEW_PERMISSION,
  PRODUCTS_VIEW_PERMISSION,
  PRODUCT_MAPPINGS_VIEW_PERMISSION,
  SUPPLIER_GROUPS_VIEW_PERMISSION,
  SUPPLIERS_VIEW_PERMISSION,
  TARIFF_DUTY_FACTORS_VIEW_PERMISSION,
  TERMINAL_EXPENSES_VIEW_PERMISSION,
  TRANSPORT_METHODS_VIEW_PERMISSION,
} from '@/core/config/auth-permissions.config';

/**
 * This app's navigation sections. The mechanism (token, provider function,
 * service, and the components that render it) lives in `@trueform/ui/layout`;
 * the route list is app data and is handed to the library through
 * `provideNavigation()` in `app.config.ts`.
 */
export const DEFAULT_APP_NAV_SECTIONS: NavSection[] = [
  {
    id: 'main',
    title: 'nav.main',
    items: [
      {
        id: 'dashboard',
        label: 'nav.dashboard',
        icon: 'lucideLayoutDashboard',
        route: '/dashboard',
      },
      {
        // The Price Hub group. Its labels come from the app-owned `prices`
        // i18n namespace (the library `nav` namespace ships inside the package
        // and cannot take new keys). The parent is hidden when the user holds
        // neither child permission (existing NavItem permission behaviour).
        id: 'market-data',
        label: 'prices.nav.marketData',
        icon: 'lucideTrendingUp',
        children: [
          {
            id: 'prices',
            label: 'prices.nav.prices',
            icon: 'lucideTrendingUp',
            route: '/prices',
            permission: PRICES_VIEW_PERMISSION,
          },
          {
            id: 'price-sources',
            label: 'prices.nav.priceSources',
            icon: 'lucideDatabase',
            route: '/price-sources',
            permission: PRICE_SOURCES_VIEW_PERMISSION,
          },
          {
            // FX Rates — third Market Data child. Labels come from the new
            // app-owned `fxRates` i18n namespace (ARCH-008 D2-b); the GROUP
            // label stays `prices.nav.marketData` (existing key, must not move).
            // `lucideTrendingUp` is already registered in `app.ts` (and is the
            // market-data group's own icon — the Prices child reuses it too);
            // registering a dedicated FX/exchange icon is outside this task's
            // scope fence.
            id: 'fx-rates',
            label: 'fxRates.nav.fxRates',
            icon: 'lucideTrendingUp',
            route: '/fx-rates',
            permission: FX_RATES_VIEW_PERMISSION,
          },
        ],
      },
      {
        id: 'access-control',
        label: 'nav.accessControl',
        icon: 'lucideShieldCheck',
        children: [
          {
            id: 'users',
            label: 'nav.users',
            icon: 'lucideUsers',
            route: '/users',
            permission: [AuthResource.NgUsers, AuthAction.View],
          },
          {
            id: 'roles',
            label: 'nav.roles',
            icon: 'lucideShieldCheck',
            route: '/roles',
            permission: [AuthResource.NgRoles, AuthAction.View],
          },
        ],
      },
      {
        id: 'catalogs',
        label: 'catalogs.nav.section',
        icon: 'lucideLayers',
        children: [
          {
            id: 'products',
            label: 'catalogs.nav.products',
            icon: 'lucidePackage',
            route: '/catalogs/products',
            permission: PRODUCTS_VIEW_PERMISSION,
          },
          {
            id: 'suppliers',
            label: 'catalogs.nav.suppliers',
            icon: 'lucideTruck',
            route: '/catalogs/suppliers',
            permission: SUPPLIERS_VIEW_PERMISSION,
          },
          {
            // SupplierGroup — the 8th catalog root (ADR-002 Q2). `lucideLayers`
            // is a registered icon that reads as "grouping"; adding a dedicated
            // group icon to `app.ts` is outside this task's scope fence.
            id: 'supplier-groups',
            label: 'catalogs.nav.supplierGroups',
            icon: 'lucideLayers',
            route: '/catalogs/supplier-groups',
            permission: SUPPLIER_GROUPS_VIEW_PERMISSION,
          },
          {
            // Currencies — the 9th catalog root (ADR-002 Q2). The registered
            // icon set (`app.ts`) has no currency-specific icon and adding one
            // is outside this task's scope fence, so the icon is pinned to
            // `lucideCreditCard` — the only money-adjacent registered icon,
            // already used by Payment Terms and Import Tax (ARCH-009 D5
            // amendment; `lucideTruck` ×3 triple-reuse precedent).
            id: 'currencies',
            label: 'catalogs.nav.currencies',
            icon: 'lucideCreditCard',
            route: '/catalogs/currencies',
            permission: CURRENCIES_VIEW_PERMISSION,
          },
          {
            // Countries — the 10th catalog root and a per-feature
            // granular nav child (ADR-018 D1): gated on its own
            // `ng.countries:view` (ARCH-020 §6-C). Placed immediately after
            // `currencies` (the reference-data cluster default, ODQ-1); the
            // `lucideGlobe` icon is dedicated geography — reusing
            // destination-cities' `lucideMapPin` would be confusingly identical
            // (ARCH-020 §12 ODQ-1), so `app.ts` registers `lucideGlobe`.
            id: 'countries',
            label: 'catalogs.nav.countries',
            icon: 'lucideGlobe',
            route: '/catalogs/countries',
            permission: COUNTRIES_VIEW_PERMISSION,
          },
          {
            id: 'hubs',
            label: 'catalogs.nav.hubs',
            icon: 'lucideWarehouse',
            route: '/catalogs/hubs',
            permission: HUBS_VIEW_PERMISSION,
          },
          {
            id: 'destination-cities',
            label: 'catalogs.nav.destinationCities',
            icon: 'lucideMapPin',
            route: '/catalogs/destination-cities',
            permission: DESTINATION_CITIES_VIEW_PERMISSION,
          },
          {
            id: 'external-contexts',
            label: 'catalogs.nav.externalContexts',
            icon: 'lucideLink2',
            route: '/catalogs/external-contexts',
            permission: EXTERNAL_CONTEXTS_VIEW_PERMISSION,
          },
          {
            // External translations — the standalone tool screen (WUIAM-145 /
            // PLAN §1.2, ODQ-2). Placed immediately after `external-contexts`
            // (contexts define the codes, translations resolve them) and gated
            // on its own `ng.product_mappings:view`; the reporting-reverse
            // toggle inside the screen is the separate `report` grant. The
            // company-less route literal is canonicalized by the layout's
            // company selector, exactly like its siblings. `lucideLayers` is
            // already registered in `app.ts` (it is the Catalogs section icon),
            // so a dedicated icon would widen the fence to `app.ts` for nothing.
            id: 'external-translations',
            label: 'catalogs.nav.externalTranslations',
            icon: 'lucideLayers',
            route: '/catalogs/external-translations',
            permission: PRODUCT_MAPPINGS_VIEW_PERMISSION,
          },
          {
            // TransportMethod — the 7th catalog root (ADR-002 Q2). `lucideTruck`
            // is the best transport-fitting icon among the ones the app already
            // registers in `app.ts` (adding a new icon is out of this task's
            // scope fence); it is also used by Suppliers, which is cosmetic.
            id: 'transport-methods',
            label: 'catalogs.nav.transportMethods',
            icon: 'lucideTruck',
            route: '/catalogs/transport-methods',
            permission: TRANSPORT_METHODS_VIEW_PERMISSION,
          },
          {
            // Packagings — the 11th company-scoped catalog root (WUIAM-130 /
            // DCS 0.4.0) and a per-feature granular nav child (ADR-018 D1):
            // gated on its own `ng.packagings:view`. Placed after the
            // transport/logistics cluster and before the tenant-scoped
            // `payment-terms`. `lucidePackage` is already registered in
            // `app.ts` (Products and Packaging Tariffs reuse it); adding a
            // dedicated icon is outside this task's scope fence.
            id: 'packagings',
            label: 'catalogs.nav.packagings',
            icon: 'lucidePackage',
            route: '/catalogs/packagings',
            permission: PACKAGINGS_VIEW_PERMISSION,
          },
          {
            id: 'payment-terms',
            label: 'catalogs.nav.paymentTerms',
            icon: 'lucideCreditCard',
            route: '/catalogs/payment-terms',
            permission: PAYMENT_TERMS_VIEW_PERMISSION,
          },
        ],
      },
      {
        // The Price Calculator group (PCS reference data). Labels come from the
        // app-owned `priceCalculator` i18n namespace; `lucideCalculator` is
        // registered in `app.ts`. The group auto-hides when the user holds no
        // child permission (existing NavItem permission behaviour).
        id: 'price-calculator',
        label: 'priceCalculator.nav.section',
        icon: 'lucideCalculator',
        children: [
          {
            id: 'ddu-tariffs',
            label: 'priceCalculator.nav.dduTariffs',
            icon: 'lucideCreditCard',
            route: '/price-calculator/ddu-tariffs',
            permission: DDU_TARIFFS_VIEW_PERMISSION,
          },
          {
            id: 'freight-tariffs',
            label: 'priceCalculator.nav.freightTariffs',
            icon: 'lucideTruck',
            route: '/price-calculator/freight-tariffs',
            permission: FREIGHT_TARIFFS_VIEW_PERMISSION,
          },
          {
            id: 'import-taxes',
            label: 'priceCalculator.nav.importTaxes',
            icon: 'lucideDatabase',
            route: '/price-calculator/import-taxes',
            permission: IMPORT_TAXES_VIEW_PERMISSION,
          },
          {
            // Packaging Tariffs — the fourth PCS tariff reference screen (ADR-016
            // D2). `lucidePackage` is already registered in `app.ts` (it is also
            // the Products icon — cosmetic reuse, matching the `lucideTruck` ×3
            // precedent).
            id: 'packaging-tariffs',
            label: 'priceCalculator.nav.packagingTariffs',
            icon: 'lucidePackage',
            route: '/price-calculator/packaging-tariffs',
            permission: PACKAGING_TARIFFS_VIEW_PERMISSION,
          },
          {
            // Payment Rates — the fifth PCS tariff reference screen (WUIAM-20 /
            // ARCH-019 §5-J). `lucideCreditCard` is already registered in
            // `app.ts` and is the money-adjacent icon (it already backs the
            // Currencies catalog and DDU Tariffs; ARCH-009 D5 invites the
            // reuse). Registering a dedicated icon is outside this task's scope
            // (ODQ-6) — the designer may swap it later with a one-line change.
            id: 'payment-term-charges',
            label: 'priceCalculator.nav.paymentTermCharges',
            icon: 'lucideCreditCard',
            route: '/price-calculator/payment-term-charges',
            permission: PAYMENT_TERM_CHARGES_VIEW_PERMISSION,
          },
          {
            // Tariff-duty factors — the sixth PCS tariff reference screen
            // (WUIAM-22 / ARCH-021 §5-I). Kept in the tariff-reference cluster,
            // before `calculation`. `lucideCreditCard` is already registered in
            // `app.ts` and is the money-adjacent icon (DDU Tariffs, Payment
            // Rates, Currencies); the designer may swap it later with a one-line
            // change (ODQ-5) — no new icon registration.
            //
            // Its OWN per-feature permission (ADR-018 D1 / TASK-312).
            id: 'tariff-duty-factors',
            label: 'priceCalculator.nav.tariffDutyFactors',
            icon: 'lucideCreditCard',
            route: '/price-calculator/tariff-duty-factors',
            permission: TARIFF_DUTY_FACTORS_VIEW_PERMISSION,
          },
          {
            // Terminal expenses — the seventh PCS tariff reference screen
            // (WUIAM-36 / ARCH-023 §5-I). Kept in the tariff-reference cluster:
            // after `tariff-duty-factors`, before `calculation`. Its OWN
            // per-feature permission (ADR-018 D1 / TASK-333).
            //
            // `lucideCreditCard` is already registered in `app.ts` (the
            // money-adjacent icon, already backing Currencies, Payment Terms,
            // DDU Tariffs, Payment Rates and Tariff-duty factors — this is the
            // 6th nav occurrence) — ODQ-5, no new icon registration; a
            // purposeful designer swap is a one-line change.
            id: 'terminal-expenses',
            label: 'priceCalculator.nav.terminalExpenses',
            icon: 'lucideCreditCard',
            route: '/price-calculator/terminal-expenses',
            permission: TERMINAL_EXPENSES_VIEW_PERMISSION,
          },
          {
            // DXV band configurations — the eighth PCS reference screen
            // (WUIAM-23 / ARCH-024 §5-I). Placed in the configurations cluster,
            // after `terminal-expenses` and before the calculators.
            // `lucideLayers` is already registered in `app.ts` and reads as a
            // stacked-bands metaphor; the designer may swap it later with a
            // one-line change (ODQ-5) — no new icon registration.
            //
            // Its OWN per-feature permission (ADR-018 D1 / TASK-354).
            id: 'dxv-configurations',
            label: 'priceCalculator.nav.dxvConfigurations',
            icon: 'lucideLayers',
            route: '/price-calculator/dxv-configurations',
            permission: DXV_CONFIGURATIONS_VIEW_PERMISSION,
          },
          {
            // IVA rates — the ninth PCS reference screen (WUIAM-111 / ARCH-035
            // §4-D). Placed in the configurations cluster, after
            // `dxv-configurations` and before the calculators (ODQ-7). Its OWN
            // per-feature permission (ADR-018 D1 / TASK-498).
            //
            // `lucideCreditCard` is already registered in `app.ts` (the
            // money-adjacent icon, also backing Currencies, Payment Terms, DDU
            // Tariffs, Payment Rates, Tariff-duty factors and Terminal expenses
            // — this is the 7th nav occurrence) — ODQ-5/7, no new icon
            // registration; a purposeful designer swap is a one-line change.
            //
            // The label sits with its eight configuration siblings in
            // `priceCalculator.nav.ivaRates` (Fix-pass Maint-4), not the
            // `pcsCalculator` namespace the iva-rates screen subtree uses.
            id: 'iva-rates',
            label: 'priceCalculator.nav.ivaRates',
            icon: 'lucideCreditCard',
            route: '/price-calculator/iva-rates',
            permission: IVA_RATES_VIEW_PERMISSION,
          },
          {
            id: 'calculation',
            label: 'pcsCalculator.nav.calculation',
            icon: 'lucideCalculator',
            route: '/price-calculator/calculation',
            permission: PCS_CALCULATOR_VIEW_PERMISSION,
          },
          {
            id: 'my-calculations',
            label: 'pcsCalculator.nav.myCalculations',
            icon: 'lucideBookmark',
            route: '/price-calculator/my-calculations',
            permission: PCS_CALCULATOR_VIEW_PERMISSION,
          },
        ],
      },
    ],
  },
];
